import React from "react";
import { Link, useLocation } from "react-router-dom";
import { AccountMenu } from "./AccountMenu.js";
import { BillingChip } from "./BillingChip.js";
import { GearIcon } from "./icons.js";
import { JobStatus } from "./JobStatus.js";
import { VeysoMark } from "./VeysoMark.js";

export const AppShell: React.FC<{
  children: React.ReactNode;
  /** Rendered on the right of the bar: the primary action for this screen. */
  actions?: React.ReactNode;
  breadcrumb?: React.ReactNode;
  /** A project's own pages (Edit · Export & share · Views), after the breadcrumb. */
  nav?: React.ReactNode;
  live?: boolean;
  /** The editor manages its own scrolling; other pages get the page container. */
  bare?: boolean;
  /**
   * A wider page container, for a screen laid out in columns. The launch
   * studio's script, film and chat sit side by side and do not fit in the
   * reading width the other pages use.
   */
  wide?: boolean;
}> = ({ children, actions, breadcrumb, nav, live, bare, wide }) => {
  // Settings belongs in the shell rather than on the dashboard: needing a key
  // is something you discover mid-edit, when a generation has just failed, and
  // routing back through the project list to fix it is a detour.
  const onSettings = useLocation().pathname === "/settings";

  return (
    <div className="flex h-full flex-col bg-page">
      {/* Wraps rather than overflows: with the project nav, a crowded bar (the
          editor's) or a phone takes a second row instead of losing the title. */}
      <header className="flex min-h-[52px] shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-[var(--edge)] px-5 py-2">
        <Link
          to="/dashboard"
          className="flex items-center gap-2.5 text-[13px] font-semibold tracking-tight text-ink-100"
        >
          <VeysoMark live={live} />
          Veyso
        </Link>
        {breadcrumb ? (
          <div className="flex min-w-0 items-center gap-2 text-[13px] text-ink-300">{breadcrumb}</div>
        ) : null}
        {nav}
        <div className="ml-auto flex items-center gap-2">
          {/* Before the page's own actions: what the app is doing outranks what
              this screen offers, and it is the thing you look for when a button
              you pressed appears to have done nothing. */}
          <JobStatus />
          {actions}
          {onSettings ? null : (
            <Link to="/settings" className="btn-icon" title="Settings" aria-label="Settings">
              <GearIcon />
            </Link>
          )}
          {/* Both of these are about the person rather than about the work,
              which is why they sit at this end. Both render nothing when there
              is no auth provider, which is how `docker compose up` keeps a
              clean bar. */}
          <BillingChip />
          <AccountMenu />
        </div>
      </header>
      {bare ? (
        children
      ) : (
        <main
          className={`mx-auto w-full flex-1 overflow-y-auto px-6 py-8 ${wide ? "max-w-[1600px]" : "max-w-[1080px]"}`}
        >
          {children}
        </main>
      )}
    </div>
  );
};
